import { useEffect } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import {DateTime} from "luxon";
import { useGetStoreEligibilityQuery, useGetStoreInfoQuery } from "app/services/api/fetchGateApi";
import { useStoreAvailabilityMutation } from "app/services/api/mutateGateAPI";
import { StoreAvailabilityType, StoreResponseType } from "app/features/Location/store/StoreResponseType";
import {Address, EntitiesEntity, Hours} from "app/features/Location/types/YextLocationResponseType";
import logging from "app/shared/utils/logging";
import {handleTimeStamp} from "app/shared/utils/helperFunctions";
import { getEnabledPickupTypeLabels } from "app/shared/utils/utilityFunctions";

interface IAdditionalParams {
    isEligible: boolean;
    timezone: string,
    storeNumber: string,
    pickupTypeOptions: string[],
    storeHours: Hours,
    description: string,
    address: Address,
    isClosedToday: boolean,
    availability?: StoreAvailabilityType[],
    cOrderAheadTempOff?: boolean,
    cOrderAhead?: boolean,
    closed?: boolean
}

type IUseStoreResult = {
    data?: StoreResponseType;
    isFetching: boolean;
    isError: boolean;
} & IAdditionalParams;

function isStoreClosedNow(storeResponse: EntitiesEntity): boolean {
    const storeHoursValue = handleTimeStamp(storeResponse, new Date());
    if (storeHoursValue?.isClosed) return true;

    const zone = storeResponse?.timezone;
    const currentTime = DateTime.local().setZone(zone);

    const startTime = DateTime.fromFormat(storeHoursValue?.start ?? "", "HH:mm", { zone });
    let endTime = DateTime.fromFormat(storeHoursValue?.end ?? "", "HH:mm", { zone });

    endTime = endTime.minus({minutes: 30});

    if (!startTime.isValid || !endTime.isValid) return true; // treat invalid times as closed

    if (endTime < startTime) {
        // Overnight (store open past midnight)
        if (currentTime >= startTime || currentTime <= endTime) return false;
    } else if (currentTime >= startTime && currentTime <= endTime) return false;
    return true;
}

function useStore(storeNumber: string): Partial<IUseStoreResult> {
    const appInsights = useAppInsightsContext();

    const eligibilityQuery = useGetStoreEligibilityQuery(storeNumber);
    const storeInfo = useGetStoreInfoQuery(storeNumber);

    const [getStoreAvailability, {
        data: availability,
        error: errorStoreAvailability,
        isLoading: isLoadingStoreAvailability,
    }] = useStoreAvailabilityMutation();

    useEffect(() => {
        if (storeInfo?.data?.response) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getStoreAvailability({
                locationId: storeNumber,
                timeZoneUtcOffset: storeInfo?.data?.response?.timeZoneUtcOffset,
                hours: storeInfo.data.response.hours,
            });
        }
    }, [storeInfo?.data?.response, storeNumber, getStoreAvailability, appInsights]);

    const queries = [
        eligibilityQuery,
        storeInfo,
    ];
    const isFetching = !!isLoadingStoreAvailability || queries.some((query) => query.isFetching);
    const error = errorStoreAvailability || queries.reduce<Error | undefined>(
        (acc, query) => acc || query.error as Error | undefined,
        undefined,
    );

    if (isFetching) return { isFetching };
    if (error) {
        logging.error(appInsights, "evt_LocationStorePage_err", `Error in API call`, error);
        return { isError: true };
    }

    const pickupTypeOptions = getEnabledPickupTypeLabels(
        storeInfo?.data?.response?.c_orderPickUp || [],
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isClosedToday = isStoreClosedNow(storeInfo?.data?.response);

    return {
        isEligible: eligibilityQuery?.data,
        isClosedToday,
        pickupTypeOptions,
        address: storeInfo?.data?.response.address,
        storeHours: storeInfo?.data?.response.hours,
        timezone: storeInfo?.data?.response.timezone,
        availability: availability?.data,
        cOrderAheadTempOff: storeInfo?.data?.response.c_orderAheadTempOff,
        cOrderAhead: storeInfo?.data?.response.c_orderAhead,
        closed: storeInfo?.data?.response.closed,
    };
}

export default useStore;
